// Data layer for the Invoices feature. Invoices ARE payments rows (see
// supabase/migrations/34_payments_as_invoices.sql): 'pending' rows are open
// invoices created with the appointment, 'completed'/'refunded' rows are paid
// receipts, 'voided' rows are kept forever. This module fetches the rows and
// shapes them into the view model both the list and the detail screens render.
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { supabase } from './supabase'
import { getCurrencySymbol } from './currencyUtils'
import { getPaymentMethodLabel } from './paymentMethodUtils'

dayjs.extend(utc)
dayjs.extend(timezone)

export async function fetchInvoices(merchantId) {
	const { data, error } = await supabase
		.from('payments')
		.select(`
			*,
			appointments (id, start_ts, end_ts, status, customer_name, user_id, group_id),
			clients (id, name, last_name, phone_number, email)
		`)
		.eq('merchant_id', merchantId)
		.not('invoice_number', 'is', null)
		.order('invoice_number', { ascending: false })
	if (error) throw error
	return data || []
}

const sum = (arr, f) => arr.reduce((s, x) => s + (f(x) || 0), 0)

// One payments row -> everything the screens need.
export function buildInvoiceView(row, merchant) {
	const tz = merchant?.timezone || 'UTC'
	const snap = row.appt_snapshot || null
	// Business identity as it was when the invoice was issued (migration 36).
	// Rows from before it fall back to the live merchant. The logo is never
	// frozen — it's branding, fetched live by the caller.
	// All-or-nothing: once a frozen block exists, every field comes from it —
	// a blank frozen phone must stay blank, not show today's number.
	const frozen = snap?.merchant || null
	const src = frozen || merchant || {}
	const biz = {
		name: src.name || '',
		address: src.address || null,
		phone: src.phone || null,
		email: src.email || null,
		currency: src.currency || null,
		taxRate: src.tax_rate != null ? Number(src.tax_rate) : null,
	}
	const cs = getCurrencySymbol(biz.currency)
	const money = (cents) => `${cs}${((cents || 0) / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`

	const appt = row.appointments || null
	const startTs = snap?.start_ts || appt?.start_ts || row.created_at
	const start = dayjs(startTs).tz(tz)
	const today = dayjs().tz(tz).startOf('day')
	const days = today.diff(start.startOf('day'), 'day') // > 0 means the appointment day has passed

	// An open invoice is Upcoming until the appointment starts, then Unpaid.
	// Compared by time, not by day, so a 2pm groom is still Upcoming at 9am.
	let status
	if (row.payment_status === 'voided') status = 'void'
	else if (row.payment_status === 'completed' || row.payment_status === 'refunded') status = 'paid'
	else status = start.isAfter(dayjs()) ? 'upcoming' : 'unpaid'

	// items grouped by pet, straight from the snapshot
	// Per pet: the service, then every add-on on its own "+" line. Duration is deliberately not shown on invoices.
	const petShape = ({ petId = null, name, breed, generic, service, addons }) => {
		const lines = [...(service ? [service] : []), ...addons]
		return { petId, name, breed, generic, service, addons, lines, subtotal: sum(lines, (l) => l.amount) }
	}
	const addonLine = (a) => ({ name: a.name || 'Add-on', amount: a.amount || 0 })
	let pets = (snap?.pets || []).map((p) => petShape({
		petId: p.pet_id || null,
		name: p.name || 'Pet', breed: p.breed || '', generic: false,
		service: p.service?.name ? { name: p.service.name, amount: p.service.price || 0 } : null,
		addons: (p.add_ons || []).map(addonLine),
	})).filter((p) => p.lines.length > 0)
	if (pets.length === 0) {
		// legacy rows without a snapshot: flat list from the payment itself
		pets = [petShape({
			name: 'Appointment', breed: '', generic: true,
			service: row.service_amount > 0 ? { name: 'Service', amount: row.service_amount } : null,
			addons: (Array.isArray(row.add_ons) ? row.add_ons : []).map(addonLine),
		})]
	}

	// totals come from the stored columns — checkout's figures are canonical
	const subtotal = (row.service_amount || 0) + sum(Array.isArray(row.add_ons) ? row.add_ons : [], (a) => a.amount)
	const discount = row.discount_amount || 0
	const discountLabel = row.discount_type === 'percent' && row.discount_value
		? `Discount (${Number(row.discount_value)}%)`
		: 'Discount'
	const tax = row.tax_amount || 0
	const taxLabel = biz.taxRate ? `Tax (${biz.taxRate}%)` : 'Tax'
	const tips = row.tips_amount || 0
	const total = row.total_amount || 0
	const paid = status === 'paid' ? total : 0
	const balance = status === 'unpaid' ? total : 0

	// getPaymentMethodLabel echoes unknown values (e.g. legacy 'stripe') — tidy those up
	const methodLabel = (m) => {
		if (!m) return 'Payment'
		if (m === 'stripe') return 'Online'
		const label = getPaymentMethodLabel(m)
		return label === m ? m.charAt(0).toUpperCase() + m.slice(1).replace(/_/g, ' ') : label
	}

	// paid_at is set by the DB when the row becomes 'completed' (migration 36);
	// older rows fall back to created_at, which for direct checkout inserts is the payment time.
	const paidAt = status === 'paid' ? dayjs(row.paid_at || row.created_at).tz(tz) : null
	// voided_at is stamped by the DB when the appointment is cancelled, deleted or marked a no-show.
	const voidedAt = status === 'void' && row.voided_at ? dayjs(row.voided_at).tz(tz) : null
	const payments = status === 'paid'
		? [{
			methodLabel: methodLabel(row.payment_method),
			dateFmt: paidAt.format('ddd, MMM D'),
			note: row.notes || null,
			amount: total,
			refunded: row.payment_status === 'refunded',
		}]
		: []

	// Client as billed. Open invoices follow the live clients row so a profile
	// correction still shows; once paid or voided the invoice is a record and
	// uses the client block frozen in the snapshot (migration 36, change 5).
	// All-or-nothing like the merchant block — a blank frozen email stays blank.
	// Rows from before the block existed keep reading the live row.
	const frozenClient = (status === 'paid' || status === 'void') ? snap?.client || null : null
	const liveName = row.clients
		? `${row.clients.name || ''}${row.clients.last_name ? ` ${row.clients.last_name}` : ''}`.trim()
		: ''
	const clientName = frozenClient
		? (frozenClient.name || 'Client')
		: (liveName || snap?.customer_name || appt?.customer_name || 'Client')
	const clientFirst = clientName.split(' ')[0]
	const clientPhone = frozenClient ? (frozenClient.phone || null) : (row.clients?.phone_number || null)
	const clientEmail = frozenClient ? (frozenClient.email || null) : (row.clients?.email || null)

	const names = pets.filter((p) => !p.generic).map((p) => p.name)
	const petNames = names.length === 0 ? '' : names.length === 1 ? names[0] : `${names.slice(0, -1).join(', ')} & ${names[names.length - 1]}`
	const breeds = [...new Set(pets.map((p) => p.breed).filter(Boolean))]
	const petLine = names.length === 0 ? '' : names.length === 1
		? `${names[0]}${breeds[0] ? ` — ${breeds[0]}` : ''}`
		: `${petNames} · ${breeds.join(', ')}`

	const rel = days === 0 ? 'Today' : days === 1 ? 'Yesterday' : days === -1 ? 'Tomorrow'
		: days > 1 ? `${days} days ago` : `In ${-days} days`
	const daysLabel = days === 0 ? 'Today' : days === 1 ? '1 day' : days > 1 ? `${days} days` : rel
	const staff = snap?.staff_name || null

	return {
		id: row.id,
		appointmentId: row.appointment_id,
		groupId: row.group_id,
		number: row.display_number || `#${row.invoice_number}`,
		status,
		isUnpaid: status === 'unpaid',
		isUpcoming: status === 'upcoming',
		isPaid: status === 'paid',
		isVoid: status === 'void',
		// Money is only ever taken at the appointment's checkout; both open it.
		canCheckout: status === 'unpaid' || status === 'upcoming',
		isRefunded: row.payment_status === 'refunded',
		voidReason: row.void_reason || null,
		voidWhat: { appointment_deleted: 'the appointment was deleted', appointment_no_show: 'the client didn’t show up' }[row.void_reason] || 'the appointment was cancelled',
		voidedAtFmt: voidedAt ? voidedAt.format('ddd, MMM D') : null,
		biz,
		clientName, clientFirst, clientPhone, clientEmail,
		pets, petNames, petLine,
		petCountLabel: names.length <= 1 ? '1 pet' : `${names.length} pets`,
		subtotal, discount, discountLabel, tax, taxLabel, tips, total, paid, balance,
		money,
		subtotalFmt: money(subtotal), discountFmt: `−${money(discount)}`, taxFmt: money(tax),
		tipsFmt: money(tips), totalFmt: money(total), paidFmt: money(paid), balanceFmt: money(balance),
		payments,
		days, rel, daysLabel,
		isAging: status === 'unpaid' && days >= 7,
		start,
		dateFmt: start.format('ddd, MMM D'),
		dateLong: start.format('ddd, MMM D, YYYY'),
		time: start.format('h:mm A'),
		paidAt,
		paidDateLong: paidAt ? paidAt.format('ddd, MMM D, YYYY') : null,
		// The document date (PDF footer, desktop header): receipts are dated by
		// payment, voided records by when they were processed (cancelled, deleted
		// or marked no-show), open invoices by the appointment.
		docDateLong: (voidedAt || paidAt || start).format('ddd, MMM D, YYYY'),
		staff,
		groomerLine: staff ? `${status === 'upcoming' ? 'With' : 'Groomed by'} ${staff}` : '',
		createdLine: `Created with the appointment · ${dayjs(row.created_at).tz(tz).format('MMM D, YYYY')}`,
		searchText: [clientName, petNames, breeds.join(' '), row.display_number, row.invoice_number, money(total), money(total).replace(cs, '')]
			.join(' ').toLowerCase(),
	}
}

const byApptAsc = (a, b) => a.start.valueOf() - b.start.valueOf()

export function splitByStatus(views) {
	const unpaid = views.filter((v) => v.isUnpaid).sort(byApptAsc) // owed longest first
	const upcoming = views.filter((v) => v.isUpcoming).sort(byApptAsc) // soonest first
	const paid = views.filter((v) => v.isPaid).sort((a, b) => -byApptAsc(a, b))
	const voided = views.filter((v) => v.isVoid).sort((a, b) => -byApptAsc(a, b))
	return { unpaid, upcoming, paid, void: voided }
}

export function invoiceTotals(byStatus, money) {
	const balanceDue = sum(byStatus.unpaid, (v) => v.balance)
	return {
		balanceDue,
		balanceDueFmt: money(balanceDue),
		unpaidCount: byStatus.unpaid.length,
		oldestDays: byStatus.unpaid.length ? Math.max(...byStatus.unpaid.map((v) => v.days)) : 0,
		upcomingCount: byStatus.upcoming.length,
		upcomingTotalFmt: money(sum(byStatus.upcoming, (v) => v.total)),
		paidCount: byStatus.paid.length,
		paidTotalFmt: money(sum(byStatus.paid, (v) => v.total)),
		voidCount: byStatus.void.length,
	}
}

export function searchInvoices(views, query) {
	const q = query.trim().toLowerCase()
	if (!q) return []
	return views
		.filter((v) => v.searchText.includes(q))
		.sort((a, b) => -byApptAsc(a, b))
}
